import { ref as dbRef, push, set, update, get, remove, onValue, query, orderByChild, equalTo, startAt, endAt } from 'firebase/database';
import { db } from '../firebase';
import { sortTimeSlots, parseTimeSlotStartHour, formatHourLabel, getCourtLabel } from './bookingUtils';
import { OPEN_PLAY_COURTS_INLINE_MAX_CHARS, OPEN_PLAY_STATUS_REMOVED, OPEN_PLAY_SESSION_STATUS_CANCELLED } from '../constants/bookingConst';
import { compressImage, userFacingError, IMAGE_PRESETS } from './imageCompressionUtil';
import { saveOpenPlayReceipt } from './receiptUtil';
import {
  logAuditEvent,
  AUDIT_ACTION_TYPES,
  ENTITY_TYPES,
  GUEST_ACTOR,
  buildOpenPlayJoinSummary,
} from './auditLogUtil';
import { formatMoney } from './currencyUtil';

// Open Play is a separate, lightweight join flow from court booking — guests
// pay per player to join a rotating-queue session for a sport instead of
// reserving a whole court, so it has its own `openPlayJoins/{id}` Firebase
// collection rather than reusing `bookings` (no court/time-slot claiming is
// involved). Mirrors the shape/lifecycle of a booking record just enough to
// reuse the same payment-countdown + receipt-upload UI pattern from
// CheckoutSummary.jsx (see OpenPlayModal.jsx).


/**
 * Creates a pending Open Play join record and returns it (including its
 * Firebase key and a short human-readable confirmationId), mirroring
 * App.jsx's createPendingBooking confirmationId/paymentDeadline scheme so
 * the same countdown/QR/upload UI can be reused.
 * @param {object} details - { sportId, sportName, date, timeSlot, sessionId, fullName, email, phone, players, numPlayers, pricePerPlayer }
 *   `players` is the optional full roster from the guest join form —
 *   [{ name, phone }], everyone named individually, with only the first
 *   required to leave a contact number. `fullName`/`phone` stay the booker's
 *   own details so every screen that already reads them (Admin -> Open Play,
 *   the host modal's player list) keeps working untouched.
 * @param {object} config - app config (for paymentTimeoutMinutes)
 * @returns {Promise<object>} the saved join record, including `id` and `confirmationId`
 */
export const createOpenPlayJoin = async (details, config) => {
  const { sportId, sportName, date, timeSlot, sessionId, fullName, email, phone, players, numPlayers, pricePerPlayer } = details;
  if (!sportId || !date || !fullName || !phone || !numPlayers) {
    throw new Error('Missing required Open Play join details.');
  }

  const confirmationId = `OP-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
  const timeoutMinutes = Number(config?.paymentTimeoutMinutes) || 5;
  const paymentDeadline = new Date(Date.now() + timeoutMinutes * 60000).toISOString();
  const totalAmount = Number(pricePerPlayer || 0) * Number(numPlayers || 0);

  const record = {
    confirmationId,
    sportId,
    sportName: sportName || '',
    date,
    // Set only when the guest joined via a specific Admin -> Manage
    // availability "Open Play" grid cell (TimeSlotSelection.jsx's
    // onJoinOpenPlay) rather than the generic landing-page join flow, which
    // only asks for a date.
    timeSlot: timeSlot || null,
    // Ties this join back to an admin-hosted openPlaySessions/{id} record
    // (see getOpenPlaySessionForCell below) when that cell was opened via
    // "Host an Open Play session" rather than the plain per-cell status
    // dropdown — used to enforce that session's own player cap.
    sessionId: sessionId || null,
    fullName,
    email: email || '',
    phone,
    // Everyone in the group by name. Firebase drops undefined values and
    // strips empty objects, so a blank optional phone is normalised to ''
    // here rather than left to disappear and reappear as undefined on read.
    ...(Array.isArray(players) && players.length > 0
      ? { players: players.map(p => ({ name: String(p?.name || '').trim(), phone: String(p?.phone || '').trim() })) }
      : {}),
    numPlayers: Number(numPlayers),
    pricePerPlayer: Number(pricePerPlayer || 0),
    totalAmount,
    // pending_payment -> pending (receipt uploaded) -> confirmed / declined.
    // 'pending_payment' is NOT a player yet: it doesn't count towards the
    // session cap and doesn't appear on any roster until a receipt is
    // attached. See ACTIVE_JOIN_STATUSES below.
    status: 'pending_payment',
    paymentDeadline,
    createdAt: new Date().toISOString(),
  };

  const joinsRef = dbRef(db, 'openPlayJoins');
  const newRef = push(joinsRef);
  await set(newRef, record);
  return { id: newRef.key, ...record };
};

/**
 * Attaches a payment receipt screenshot to an Open Play join and flips its
 * status to 'pending' (awaiting admin review) — same base64-in-RTDB approach
 * as imageUploadUtil.js's uploadPaymentScreenshot, kept separate since it
 * targets `openPlayJoins` instead of `bookings`.
 * @param {File} file
 * @param {string} confirmationId
 * @returns {Promise<string>} the base64 data URL of the uploaded image
 */
export const uploadOpenPlayScreenshot = async (file, confirmationId) => {
  if (!file || !confirmationId) {
    throw new Error('File and join confirmation ID are required');
  }

  // Downscaled and re-encoded before storing — the raw file used to go in at
  // up to 2000KB, and this record is read back in full by the admin Open Play
  // screen. See imageCompressionUtil.js. Validates the file type too.
  const { dataUrl: base64Data } = await compressImage(file, IMAGE_PRESETS.RECEIPT);

  const joinsRef = dbRef(db, 'openPlayJoins');
  const q = query(joinsRef, orderByChild('confirmationId'), equalTo(confirmationId));
  const snapshot = await get(q);
  if (!snapshot.exists()) {
    throw userFacingError('Open Play join not found. Please check the confirmation ID.');
  }
  // The image goes to its own node (openPlayReceipts/{joinId}) and the join
  // keeps only a flag — the admin Open Play screen and the reservation queue
  // read these records in bulk and were downloading every receipt just to
  // render a list. saveOpenPlayReceipt writes the image, the flag and the
  // status as one atomic update, so a paid join can never be left invisible
  // to review by a half-applied write.
  const keys = [];
  const records = {};
  snapshot.forEach((child) => { keys.push(child.key); records[child.key] = child.val(); });
  await Promise.all(keys.map(key => saveOpenPlayReceipt(key, base64Data, 'pending')));

  // Audit trail — logged HERE rather than in createOpenPlayJoin, for the
  // same reason a court booking is logged at receipt upload: a join sitting
  // at 'pending_payment' is an open checkout, not a request. It counts
  // towards nothing, appears on no roster, and is routinely abandoned or
  // discarded outright (see discardUnpaidOpenPlayJoin). Attaching the
  // receipt is the point at which someone has actually paid to join.
  keys.forEach((key) => {
    const rec = records[key] || {};
    logAuditEvent({
      actionType: AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_JOINED,
      entityType: ENTITY_TYPES.OPENPLAY_JOIN,
      entityId: key,
      performedBy: rec.fullName || GUEST_ACTOR,
      statusOld: rec.status || 'pending_payment',
      statusNew: 'pending',
      summary: buildOpenPlayJoinSummary(rec),
    });
  });

  return base64Data;
};

/**
 * Throws away a join that never got paid for.
 *
 * Used when a guest backs out of the checkout step to change their player
 * list: without this, pressing Continue again would write a SECOND join
 * record and the first would linger as a ghost the admin might one day see.
 *
 * Refuses to touch anything that isn't still 'pending_payment' — once a
 * receipt is attached the record is evidence of a payment, and no
 * navigation should be able to delete it.
 *
 * @param {string} joinId - Firebase key of the openPlayJoins record
 * @returns {Promise<boolean>} true when a record was actually removed
 */
export const discardUnpaidOpenPlayJoin = async (joinId) => {
  if (!joinId || !db) return false;
  const joinRef = dbRef(db, `openPlayJoins/${joinId}`);
  const snapshot = await get(joinRef);
  if (!snapshot.exists() || snapshot.val()?.status !== 'pending_payment') return false;
  await remove(joinRef);
  return true;
};

/**
 * Finds a single Open Play join by the reference the guest was given.
 *
 * Exists so the Booking Status screen can look up a join with the same code
 * a court booking is looked up by — before this, an Open Play reference
 * returned "no booking found", which reads as "your payment vanished" to
 * the one guest most likely to be checking.
 *
 * @param {string} confirmationId
 * @returns {Promise<object|null>} the join record with its Firebase `id`, or
 *   null when nothing matches
 */
export const findOpenPlayJoinByConfirmationId = async (confirmationId) => {
  if (!confirmationId || !db) return null;
  const q = query(dbRef(db, 'openPlayJoins'), orderByChild('confirmationId'), equalTo(confirmationId));
  const snapshot = await get(q);
  if (!snapshot.exists()) return null;
  // A confirmationId is unique per join, so the first hit is the answer.
  let found = null;
  snapshot.forEach((child) => {
    if (!found) found = { id: child.key, ...child.val() };
  });
  return found;
};

/**
 * Admin action: confirm or decline a pending Open Play join.
 * @param {string} joinId - Firebase key of the openPlayJoins record
 * @param {'confirmed'|'declined'} status
 */
export const setOpenPlayJoinStatus = async (joinId, status) => {
  if (!joinId || !['confirmed', 'declined'].includes(status)) {
    throw new Error('Invalid Open Play status update.');
  }
  // Read first so the audit entry can carry the actual prior status and a
  // guest-name/session summary — update() alone wouldn't have either.
  const joinRef = dbRef(db, `openPlayJoins/${joinId}`);
  const before = await get(joinRef);
  const beforeData = before.exists() ? before.val() : null;

  await update(joinRef, { status });

  logAuditEvent({
    actionType: status === 'confirmed'
      ? AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_CONFIRMED
      : AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_DECLINED,
    entityType: ENTITY_TYPES.OPENPLAY_JOIN,
    entityId: joinId,
    statusOld: beforeData?.status || null,
    statusNew: status,
    summary: buildOpenPlayJoinSummary(beforeData),
  });
};

// ---- Overnight sessions ---------------------------------------------------
//
// A session may run past midnight (10PM -> 6AM). It stays ONE record, stored
// against the date it STARTS on, and splits its hours across two fields:
//
//   date            "2026-09-02"  the night it starts
//   courts          { [court]: [slots on `date`] }         10PM, 11PM
//   endDate         "2026-09-03"  (absent when it doesn't cross midnight)
//   courtsNextDay   { [court]: [slots on `endDate`] }      12AM..5AM
//
// `courts` keeps exactly the meaning it always had, so every reader written
// before overnight existed still sees a correct — if now partial — session
// rather than a mangled one. Anything that needs the whole picture goes
// through the helpers below.
//
// The split is stored rather than derived from the hours themselves: an
// admin can legitimately pick 1AM and 10PM on the same date as two separate
// blocks, and no rule over the hour numbers alone can tell that apart from
// one block running 10PM into the next morning.

/** "2026-09-02" -> "2026-09-03". Parsed as a local date, never UTC. */
export const nextDateStr = (dateStr) => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d + 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

/** "2026-09-03" -> "2026-09-02". */
export const previousDateStr = (dateStr) => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d - 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

/** Does this session occupy courts on `dateStr` — as its start OR its end? */
export const sessionCoversDate = (session, dateStr) =>
  !!dateStr && (session?.date === dateStr || session?.endDate === dateStr);

/**
 * The { [court]: timeSlot[] } this session holds ON ONE DATE.
 *
 * A session that doesn't cross midnight answers with `courts` for its own
 * date and {} for anything else, which is what every caller did inline
 * before overnight sessions existed.
 *
 * @param {object} session - an openPlaySessions record
 * @param {string} dateStr - "yyyy-MM-dd"
 * @returns {Record<string, string[]>} keyed by court number as a STRING
 */
export const getSessionSlotsForDate = (session, dateStr) => {
  const source = session?.date === dateStr
    ? session?.courts
    : (session?.endDate === dateStr ? session?.courtsNextDay : null);
  if (!source) return {};
  return Object.fromEntries(
    Object.entries(source)
      .filter(([, slots]) => Array.isArray(slots) && slots.length > 0)
      .map(([court, slots]) => [String(court), slots]),
  );
};

/**
 * Every court/hour pair the session holds across BOTH dates, as
 * { [court]: [{ slot, dateStr }] } — for the places that need the session
 * whole (its overall time range, clearing its overrides on edit).
 *
 * @param {object} session
 * @returns {Record<string, Array<{slot: string, dateStr: string}>>}
 */
export const getSessionSlotsWithDates = (session) => {
  const out = {};
  const add = (map, dateStr) => {
    Object.entries(map || {}).forEach(([court, slots]) => {
      (slots || []).forEach((slot) => {
        const key = String(court);
        if (!out[key]) out[key] = [];
        out[key].push({ slot, dateStr });
      });
    });
  };
  add(session?.courts, session?.date);
  if (session?.endDate) add(session?.courtsNextDay, session.endDate);
  return out;
};

/**
 * One hosted session by its Firebase key.
 *
 * A JOIN records the single grid cell the guest tapped (`timeSlot`), not the
 * session's hours — so any screen that wants to say what the guest actually
 * bought has to come back here for the session. Reading it live rather than
 * copying the range onto the join keeps it right when the admin later edits
 * the session's hours.
 *
 * @param {string} sessionId
 * @returns {Promise<object|null>} the session record with its `id`, or null
 */
export const getOpenPlaySessionById = async (sessionId) => {
  if (!sessionId) return null;
  const snap = await get(dbRef(db, `openPlaySessions/${sessionId}`));
  return snap.exists() ? { id: sessionId, ...snap.val() } : null;
};

/**
 * Looks up the admin-hosted Open Play session (if any) that covers one
 * specific grid cell — see AdminHostOpenPlayModal.jsx, which writes an
 * openPlaySessions/{id} record with a `courts: { [court]: timeSlot[] }`
 * map alongside the slotOverrides entries it sets. OpenPlayModal.jsx uses
 * this to show that session's own fee/notes/player cap instead of the
 * sport-level defaults, and to enforce the cap.
 *
 * Returns null when the cell's "Open Play" status was set via the plain
 * per-cell dropdown instead of "Host an Open Play session" — the guest
 * still gets to join, just against the sport's generic openPlayPricePerPlayer/
 * openPlayNote/no cap, exactly like before this feature existed.
 *
 * @param {string} dateStr - "yyyy-MM-dd"
 * @param {string} timeSlot
 * @param {number|string} court
 * @returns {Promise<object|null>} the session record (with `id`), or null
 */
export const getOpenPlaySessionForCell = async (dateStr, timeSlot, court) => {
  if (!dateStr || !timeSlot || court === undefined || court === null) return null;
  const sessionsRef = dbRef(db, 'openPlaySessions');
  // Two days are searched, not one: an OVERNIGHT session is stored against
  // the date it STARTED on, so the cell a guest tapped at 2AM belongs to a
  // session dated yesterday. A ranged query on the existing `date` index
  // covers both — deliberately not a second query on `endDate`, which would
  // need its own ".indexOn" in the database rules to work at all.
  const q = query(sessionsRef, orderByChild('date'), startAt(previousDateStr(dateStr)), endAt(dateStr));
  const snapshot = await get(q);
  if (!snapshot.exists()) return null;

  let match = null;
  snapshot.forEach((child) => {
    const session = child.val();
    // getSessionSlotsForDate picks the right half of the session for this
    // date, so a session running 10PM->6AM matches its 11PM cell only on the
    // start date and its 2AM cell only on the end date — never both.
    // Cancelled sessions are skipped: cancelling clears the cell's override
    // so nothing should reach here, but a stale grid in an open tab could
    // still ask, and answering with a dead session would put a guest into a
    // checkout for a session that is no longer running.
    if (session?.status !== 'active') return;
    const courtSlots = getSessionSlotsForDate({ ...session }, dateStr)[String(court)];
    if (Array.isArray(courtSlots) && courtSlots.includes(timeSlot)) {
      match = { id: child.key, ...session };
    }
  });
  return match;
};

// Which joins count as PLAYERS — the "X / max joined" capacity figure, the
// admin's player list, and the guest-facing counter all read this.
//
// A join only becomes a player once a payment receipt is attached
// (uploadOpenPlayScreenshot flips 'pending_payment' -> 'pending'). Before
// that it is someone who opened the checkout modal and may never come back:
// an abandoned cart, not an attendee. Counting those made the session look
// fuller than it was and, once the payment window lapsed, left phantom names
// on the roster that nobody ever removed.
//
// 'pending' (paid, awaiting the admin's review) DOES count and DOES appear —
// the money is in, so the spot is spoken for — and the host modal marks it
// with a dot rather than hiding it. Declining sends the join to 'declined',
// which drops out of this list and therefore off the roster and the count,
// freeing the spot again.
//
// TRADE-OFF: a spot is no longer held during the payment countdown, so two
// guests checking out simultaneously can both pay for the last place. That
// is the deliberate cost of not showing unpaid joins as attendees; the admin
// resolves it at review time by declining one, which returns the spot.
const ACTIVE_JOIN_STATUSES = ['pending', 'confirmed'];

/**
 * Sums numPlayers across every PAID join tied to a hosted session (see
 * ACTIVE_JOIN_STATUSES) — the "X / max joined" capacity figure. A one-shot
 * read; OpenPlayModal.jsx subscribes instead so the count keeps moving while
 * a guest is filling the form in.
 * @param {string} sessionId
 * @returns {Promise<number>}
 */
export const countSessionJoinedPlayers = async (sessionId) => {
  if (!sessionId) return 0;
  const joinsRef = dbRef(db, 'openPlayJoins');
  const q = query(joinsRef, orderByChild('sessionId'), equalTo(sessionId));
  const snapshot = await get(q);
  if (!snapshot.exists()) return 0;
  let total = 0;
  snapshot.forEach((child) => {
    const join = child.val();
    if (ACTIVE_JOIN_STATUSES.includes(join?.status)) {
      total += Number(join.numPlayers) || 0;
    }
  });
  return total;
};

/**
 * Live-subscribes to the players on one hosted session, newest last — the
 * admin's list inside AdminHostOpenPlayModal and the guest-facing counter in
 * OpenPlayModal. Filtered to ACTIVE_JOIN_STATUSES, so a guest who is still
 * mid-checkout never appears, and a declined join disappears from the list
 * the moment the admin declines it.
 * @param {string} sessionId
 * @param {(joins: object[]) => void} callback - each join includes its Firebase `id`
 * @returns {() => void} unsubscribe (a no-op when sessionId is falsy)
 */
export const subscribeSessionJoins = (sessionId, callback) => {
  if (!sessionId) {
    callback([]);
    return () => {};
  }
  const joinsRef = dbRef(db, 'openPlayJoins');
  const q = query(joinsRef, orderByChild('sessionId'), equalTo(sessionId));
  const unsubscribe = onValue(q, (snapshot) => {
    const joins = [];
    snapshot.forEach((child) => {
      const join = child.val();
      if (ACTIVE_JOIN_STATUSES.includes(join?.status)) joins.push({ id: child.key, ...join });
    });
    joins.sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')));
    callback(joins);
  }, (err) => {
    console.error('Error listening to Open Play session joins:', err);
    callback([]);
  });
  return unsubscribe;
};

/**
 * Admin adds a player (or a group under one name) to a hosted session
 * directly — a walk-in or someone who paid in person, as opposed to the
 * guest-facing self-service join flow in OpenPlayModal.jsx. Written
 * straight to 'confirmed' since an admin entering it IS the verification;
 * there's no receipt to review. Flagged `addedByAdmin` so these stay
 * distinguishable from self-service joins later.
 * @param {{sessionId:string, sportId:string, sportName:string, date:string, fullName:string, numPlayers:number, pricePerPlayer:number}} details
 * @returns {Promise<object>} the saved join record, including its `id`
 */
export const addManualOpenPlayJoin = async (details) => {
  const { sessionId, sportId, sportName, date, fullName, numPlayers, pricePerPlayer } = details;
  if (!sessionId || !fullName?.trim() || !numPlayers || Number(numPlayers) < 1) {
    throw new Error('A name and at least 1 player are required.');
  }
  const count = Number(numPlayers);
  const record = {
    confirmationId: `OP-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
    sessionId,
    sportId: sportId || '',
    sportName: sportName || '',
    date: date || '',
    timeSlot: null,
    fullName: fullName.trim(),
    email: '',
    phone: '',
    numPlayers: count,
    pricePerPlayer: Number(pricePerPlayer) || 0,
    totalAmount: (Number(pricePerPlayer) || 0) * count,
    status: 'confirmed',
    addedByAdmin: true,
    createdAt: new Date().toISOString(),
  };
  const newRef = push(dbRef(db, 'openPlayJoins'));
  await set(newRef, record);
  logAuditEvent({
    actionType: AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_JOINED,
    entityType: ENTITY_TYPES.OPENPLAY_JOIN,
    entityId: newRef.key,
    statusNew: record.status,
    summary: buildOpenPlayJoinSummary(record),
  });
  return { id: newRef.key, ...record };
};

/**
 * Takes specific PEOPLE off a confirmed join, leaving the rest of the group
 * on the session.
 *
 * A guest who books for four is one record with a four-name roster, so
 * "cancel one person" is an edit to that record, not a status change: the
 * named player comes out of `players`, `numPlayers` drops by one, and that
 * one spot returns to the session's cap. Only when the LAST person goes does
 * the join itself become 'removed'.
 *
 * `paidPlayers` is stamped on the first removal and never moves afterwards.
 * The club banked four fees whether or not the fourth turns up, so the export
 * bills from that instead of the shrinking head count — otherwise taking
 * someone off the roster would quietly rewrite last week's revenue.
 *
 * `removedPlayers` keeps who left and when. Without it the name simply
 * vanishes and nobody can answer "who did we take off?" a day later.
 *
 * `fullName` deliberately stays put even when the booker is the one removed:
 * it is the payer and the contact for the whole join, which is still true
 * once they stop playing.
 *
 * A join with no roster at all (an admin walk-in, or one saved before
 * rosters existed) has no person to name, so this falls through to removing
 * the whole booking.
 *
 * @param {string} joinId
 * @param {number[]} playerIndexes - positions in the join's `players` roster
 * @returns {Promise<void>}
 */
export const removeOpenPlayPlayers = async (joinId, playerIndexes) => {
  if (!joinId) throw new Error('Join id is required.');
  const indexes = [...new Set((playerIndexes || []).map(Number))].filter(i => Number.isInteger(i) && i >= 0);
  if (indexes.length === 0) return;

  const joinRef = dbRef(db, `openPlayJoins/${joinId}`);
  const before = await get(joinRef);
  if (!before.exists()) throw new Error('That player is no longer on this session.');
  const beforeData = before.val();

  const roster = Array.isArray(beforeData.players) ? beforeData.players : [];
  const survivors = roster.filter((_, i) => !indexes.includes(i));
  const removed = roster.filter((_, i) => indexes.includes(i));

  // No roster to edit, or nobody left on it — either way the whole join
  // comes off, which is what removeOpenPlayJoin already does correctly
  // (including its audit entry for the booking as a whole).
  if (roster.length === 0 || survivors.length === 0) {
    await removeOpenPlayJoin(joinId);
    return;
  }

  await update(joinRef, {
    players: survivors,
    numPlayers: survivors.length,
    // ?? not ||: a stamped 0 would be wrong to overwrite, and the first
    // removal is the only moment the pre-removal head count is still known.
    paidPlayers: beforeData.paidPlayers ?? beforeData.numPlayers ?? roster.length,
    removedPlayers: [
      ...(Array.isArray(beforeData.removedPlayers) ? beforeData.removedPlayers : []),
      ...removed.map(p => ({ name: String(p?.name || '').trim(), removedAt: new Date().toISOString() })),
    ],
  });

  logAuditEvent({
    actionType: AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_REMOVED,
    entityType: ENTITY_TYPES.OPENPLAY_JOIN,
    entityId: joinId,
    // The join keeps its status — only some of its people left — so the
    // Old -> New column would read "confirmed -> confirmed". Left null so
    // the trail shows this for what it is: an edit, not a status change.
    statusOld: null,
    statusNew: null,
    // Summarised from a record carrying ONLY the people who left, so the
    // entry names exactly who was taken off rather than the whole group.
    summary: buildOpenPlayJoinSummary({ ...beforeData, players: removed, numPlayers: removed.length }),
  });
};

/**
 * Takes a confirmed player back off a hosted session, WITHOUT deleting them.
 *
 * Writes OPEN_PLAY_STATUS_REMOVED, which sits outside ACTIVE_JOIN_STATUSES —
 * so the join drops off the roster and its spots return to the session's cap
 * as soon as this lands. No separate capacity bookkeeping: the same filter
 * that already frees a declined join's spots frees these.
 *
 * A status rather than a delete (deleteOpenPlayJoin below) because a
 * confirmed player has PAID. Their record is what a refund, a dispute, or
 * the guest opening their own reference has to go on, and deleting it throws
 * the receipt away along with the roster entry.
 *
 * @param {string} joinId
 * @returns {Promise<void>}
 */
export const removeOpenPlayJoin = async (joinId) => {
  if (!joinId) throw new Error('Join id is required.');
  const joinRef = dbRef(db, `openPlayJoins/${joinId}`);
  // Read first so the audit entry can carry the actual prior status and a
  // guest-name/session summary — update() alone wouldn't have either.
  const before = await get(joinRef);
  const beforeData = before.exists() ? before.val() : null;

  await update(joinRef, { status: OPEN_PLAY_STATUS_REMOVED });

  logAuditEvent({
    actionType: AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_REMOVED,
    entityType: ENTITY_TYPES.OPENPLAY_JOIN,
    entityId: joinId,
    statusOld: beforeData?.status || null,
    statusNew: OPEN_PLAY_STATUS_REMOVED,
    summary: buildOpenPlayJoinSummary(beforeData),
  });
};

/**
 * Deletes a join outright, leaving no record at all.
 * @param {string} joinId
 */
export const deleteOpenPlayJoin = async (joinId) => {
  if (!joinId) throw new Error('Join id is required.');
  const joinRef = dbRef(db, `openPlayJoins/${joinId}`);
  // Read first — once removed there's nothing left to build the audit
  // entry's summary/statusOld from.
  const before = await get(joinRef);
  const beforeData = before.exists() ? before.val() : null;

  await remove(joinRef);

  logAuditEvent({
    actionType: AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_REMOVED,
    entityType: ENTITY_TYPES.OPENPLAY_JOIN,
    entityId: joinId,
    statusOld: beforeData?.status || null,
    statusNew: null,
    summary: buildOpenPlayJoinSummary(beforeData),
  });
};

/**
 * Live-subscribes to every openPlaySessions record (admin-hosted or
 * otherwise), for the "Hosted open play sessions" card list shown on both
 * the Admin -> Manage availability screen and the guest Time Slot Selection
 * screen (see OpenPlaySessionsSection.jsx). Callers filter/sort the raw
 * list themselves (by date, sport, status) since what counts as "upcoming"
 * differs slightly by caller.
 * Pass `fromDate` ('yyyy-MM-dd') to have Firebase bound the result to that
 * date onwards instead of returning every session ever hosted. The guest
 * booking screen mounts this on every page load and only ever renders
 * upcoming sessions, so without the bound each visitor was downloading years
 * of dead records to throw them away client-side. Callers still apply their
 * own filter, which also covers a tab left open across midnight.
 * Requires .indexOn: ["date"] on /openPlaySessions.
 *
 * @param {(sessions: object[]) => void} callback - receives each session with its Firebase `id`
 * @param {{fromDate?: string}} [options]
 * @returns {() => void} unsubscribe
 */
export const subscribeOpenPlaySessions = (callback, { fromDate } = {}) => {
  const sessionsRef = dbRef(db, 'openPlaySessions');
  const sessionsQuery = fromDate
    ? query(sessionsRef, orderByChild('date'), startAt(fromDate))
    : sessionsRef;
  const unsubscribe = onValue(sessionsQuery, (snapshot) => {
    const sessions = [];
    snapshot.forEach((child) => {
      sessions.push({ id: child.key, ...child.val() });
    });
    callback(sessions);
  }, (err) => {
    console.error('Error listening to Open Play sessions:', err);
    callback([]);
  });
  return unsubscribe;
};

/**
 * Live "what Open Play is happening on this date" view: every ACTIVE session
 * hosted that day, each with its paid joins attached as a `joins` array
 * (ACTIVE_JOIN_STATUSES, so pending and confirmed — callers narrow further).
 *
 * Fans out over subscribeSessionJoins per session rather than querying
 * openPlayJoins by date: that node is indexed on `sessionId` and `createdAt`,
 * not on `date`, so a date query there would make Firebase download the whole
 * collection and filter it client-side. A single day holds a handful of
 * sessions at most, so the fan-out is much cheaper than the missing index.
 *
 * @param {string} dateStr - "yyyy-MM-dd"
 * @param {(sessions: object[]) => void} callback - each session with a `joins` array
 * @returns {() => void} unsubscribe (a no-op when dateStr is falsy)
 */
export const subscribeOpenPlayDay = (dateStr, callback) => {
  if (!dateStr) {
    callback([]);
    return () => {};
  }

  let sessions = [];
  const joinsBySession = {};
  const joinUnsubs = new Map();

  const emit = () => callback(sessions.map(session => ({
    ...session,
    joins: joinsBySession[session.id] || [],
  })));

  const unsubscribeSessions = subscribeOpenPlaySessions((all) => {
    sessions = all
      // sessionCoversDate, not `date === dateStr`: an overnight session is
      // stored against the night it started, and the morning it runs into
      // needs to list it too — that day's grid is showing its cells.
      .filter(session => sessionCoversDate(session, dateStr) && session.status === 'active')
      .sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')));

    // Tear down the per-session join listeners for sessions that are no
    // longer part of this day (deleted, or moved to another date) — without
    // this, browsing across dates leaves a growing pile of live listeners.
    const liveIds = new Set(sessions.map(session => session.id));
    joinUnsubs.forEach((unsubscribeJoins, sessionId) => {
      if (liveIds.has(sessionId)) return;
      unsubscribeJoins();
      joinUnsubs.delete(sessionId);
      delete joinsBySession[sessionId];
    });

    sessions.forEach((session) => {
      if (joinUnsubs.has(session.id)) return;
      joinUnsubs.set(session.id, subscribeSessionJoins(session.id, (joins) => {
        joinsBySession[session.id] = joins;
        emit();
      }));
    });

    emit();
    // One day EARLIER than the date being viewed: a session that started
    // last night and runs into this morning is stored under yesterday's
    // date, so a bound of `dateStr` would filter it out server-side before
    // sessionCoversDate ever got to see it.
  }, { fromDate: previousDateStr(dateStr) });

  return () => {
    unsubscribeSessions();
    joinUnsubs.forEach(unsubscribeJoins => unsubscribeJoins());
    joinUnsubs.clear();
  };
};

/**
 * Creates a new Open Play session, or edits an existing one in place, as a
 * single atomic multi-path Firebase update covering both the
 * openPlaySessions/{id} record and every slotOverrides/{date}/{slot}/{court}
 * entry it needs to set to 'openPlay' — same write shape
 * AdminHostOpenPlayModal always used for creation, now shared with editing.
 *
 * Editing first clears every (date/court/slot) cell the PREVIOUS version of
 * this session held (set back to null, i.e. no override) before writing the
 * new selection, so a court/slot the admin removed from the session reverts
 * to its normal open/booked/passed status instead of staying stuck showing
 * "Open Play". A cell kept in both the old and new version is simply
 * cleared then immediately re-set to 'openPlay' by the same update object
 * (last write for a given path wins), so it ends up correct either way.
 *
 * @param {object|null} existingSession - the session being edited (must include its `id` and original `date`/`courts`), or null to create a new one
 * @param {{sportId:string, sportName:string, dateStr:string, courtSlots:Record<string,string[]>, maxPlayers:number, fee:number, notes:string}} details
 * @returns {Promise<string>} the session's Firebase key
 */
export const saveOpenPlaySession = async (existingSession, details) => {
  const { sportId, sportName, dateStr, courtSlots, courtSlotsNextDay, maxPlayers, fee, notes } = details;
  const entries = Object.entries(courtSlots || {}).filter(([, slots]) => (slots || []).length > 0);
  // The hours after midnight, which land on the NEXT date. A session may be
  // entirely on this side of midnight (the common case, empty here) but never
  // entirely on the other: the picker anchors every range to the start date.
  const nextEntries = Object.entries(courtSlotsNextDay || {}).filter(([, slots]) => (slots || []).length > 0);
  if (entries.length === 0 && nextEntries.length === 0) {
    throw new Error('Select at least one court and its time slots.');
  }
  if (!maxPlayers || Number(maxPlayers) < 1) {
    throw new Error('Set a max number of players.');
  }

  const endDateStr = nextEntries.length > 0 ? nextDateStr(dateStr) : null;
  const updates = {};

  if (existingSession) {
    // Cleared across BOTH of the old version's dates. An edit that shortens
    // an overnight session back to one that ends at midnight would otherwise
    // leave the next morning's cells stuck showing Open Play with no session
    // behind them — unbookable, and invisible to the admin who did it.
    Object.entries(getSessionSlotsWithDates(existingSession)).forEach(([court, slots]) => {
      slots.forEach(({ slot, dateStr: slotDate }) => {
        updates[`slotOverrides/${slotDate}/${slot}/${court}`] = null;
      });
    });
  }

  const courtsPayload = {};
  entries.forEach(([court, slots]) => {
    courtsPayload[court] = slots;
    slots.forEach((slot) => {
      updates[`slotOverrides/${dateStr}/${slot}/${court}`] = 'openPlay';
    });
  });

  const courtsNextDayPayload = {};
  nextEntries.forEach(([court, slots]) => {
    courtsNextDayPayload[court] = slots;
    slots.forEach((slot) => {
      updates[`slotOverrides/${endDateStr}/${slot}/${court}`] = 'openPlay';
    });
  });

  const sessionId = existingSession?.id || push(dbRef(db, 'openPlaySessions')).key;
  updates[`openPlaySessions/${sessionId}`] = {
    sportId,
    sportName: sportName || '',
    date: dateStr,
    courts: courtsPayload,
    // Written as null rather than omitted when the session doesn't cross
    // midnight, so EDITING an overnight session back to a same-day one
    // actually removes the old endDate instead of leaving it behind (a
    // multi-path update replaces this whole node, but the explicit null
    // keeps that true if the write is ever narrowed to single fields).
    endDate: endDateStr,
    courtsNextDay: nextEntries.length > 0 ? courtsNextDayPayload : null,
    maxPlayers: Number(maxPlayers),
    fee: Number(fee) || 0,
    notes: (notes || '').trim(),
    status: 'active',
    createdAt: existingSession?.createdAt || new Date().toISOString(),
    ...(existingSession ? { updatedAt: new Date().toISOString() } : {}),
  };

  await update(dbRef(db), updates);

  logAuditEvent({
    actionType: existingSession
      ? AUDIT_ACTION_TYPES.OPENPLAY_SESSION_EDITED
      : AUDIT_ACTION_TYPES.OPENPLAY_SESSION_CREATED,
    entityType: ENTITY_TYPES.OPENPLAY_SESSION,
    entityId: sessionId,
    summary: {
      sportName: sportName || '',
      date: dateStr,
      // The full { [court]: [timeSlot, ...] } map, not just the court
      // numbers — same field name and shape a booking's summary uses, so the
      // Audit Trail renders a session's courts and hours with exactly the
      // same "Court N: <hours>" lines instead of a second special case.
      // Both dates' hours merged, so the Audit Trail's "Court N: <hours>"
      // line describes the whole session rather than the part of it that
      // happened to fall before midnight.
      slotsByCourt: [...new Set([...Object.keys(courtsPayload), ...Object.keys(courtsNextDayPayload)])]
        .reduce((acc, court) => {
          acc[court] = [...(courtsPayload[court] || []), ...(courtsNextDayPayload[court] || [])];
          return acc;
        }, {}),
      endDate: endDateStr,
      maxPlayers: Number(maxPlayers),
      fee: Number(fee) || 0,
      type: 'openplay-session',
    },
    // A session edit can change which courts/slots it covers — the same
    // "snapshot of what changed" gap flagged for bookings. Cheap to capture
    // here since both shapes are already in hand.
    changes: existingSession ? {
      before: {
        courts: existingSession.courts || {},
        courtsNextDay: existingSession.courtsNextDay || {},
        maxPlayers: existingSession.maxPlayers,
        fee: existingSession.fee,
      },
      after: {
        courts: courtsPayload,
        courtsNextDay: courtsNextDayPayload,
        maxPlayers: Number(maxPlayers),
        fee: Number(fee) || 0,
      },
    } : null,
  });

  return sessionId;
};

/**
 * Calls off a hosted session and everyone on it, in one atomic write.
 *
 * CANCELLED, NOT DELETED. Every join under a session is a receipt for money
 * already taken, so the records stay: a refund, a chargeback or a guest
 * opening their own booking reference all need them. The session is flipped
 * to OPEN_PLAY_SESSION_STATUS_CANCELLED, which is enough to remove it from
 * every screen — the admin rail, the guest rail and the availability grid all
 * already filter on `status === 'active'`.
 *
 * Three things have to move together, which is why this is one multi-path
 * update rather than a loop of writes:
 *   1. the slotOverrides cells go back to null, across BOTH dates, so the
 *      courts are immediately bookable again (an overnight session holds
 *      cells on the following date too);
 *   2. the session record is marked cancelled;
 *   3. every ACTIVE join is written to OPEN_PLAY_STATUS_REMOVED.
 * A partial failure here would be the worst outcome available — courts freed
 * while the session still looks live, or players cancelled on a session that
 * is still taking joins — so Firebase either applies all of it or none.
 *
 * Joins reuse OPEN_PLAY_STATUS_REMOVED rather than a status of their own:
 * it already sits outside ACTIVE_JOIN_STATUSES, already counts as voided in
 * the export, and already renders in the audit trail. A new join status would
 * have needed all three taught about it for no gain the session's own audit
 * entry doesn't already provide.
 *
 * @param {object} session - the session record, including its Firebase `id`
 * @returns {Promise<{joins: number, players: number}>} how much was cancelled
 */
export const cancelOpenPlaySession = async (session) => {
  const sessionId = session?.id;
  if (!sessionId) throw new Error('Session id is required.');

  // Read the joins BEFORE the write: afterwards their prior statuses are gone
  // and the audit entries would have nothing to record.
  const joinsQuery = query(dbRef(db, 'openPlayJoins'), orderByChild('sessionId'), equalTo(sessionId));
  const joinsSnapshot = await get(joinsQuery);
  const affected = [];
  joinsSnapshot.forEach((child) => {
    const join = child.val();
    if (ACTIVE_JOIN_STATUSES.includes(join?.status)) affected.push({ id: child.key, ...join });
  });

  const updates = {};
  Object.entries(getSessionSlotsWithDates(session)).forEach(([court, slots]) => {
    slots.forEach(({ slot, dateStr }) => {
      updates[`slotOverrides/${dateStr}/${slot}/${court}`] = null;
    });
  });
  updates[`openPlaySessions/${sessionId}/status`] = OPEN_PLAY_SESSION_STATUS_CANCELLED;
  updates[`openPlaySessions/${sessionId}/cancelledAt`] = new Date().toISOString();
  affected.forEach((join) => {
    updates[`openPlayJoins/${join.id}/status`] = OPEN_PLAY_STATUS_REMOVED;
  });

  await update(dbRef(db), updates);

  const players = affected.reduce((sum, join) => sum + (Number(join.numPlayers) || 0), 0);

  // Logged only after the write lands — an entry for a cancellation that
  // failed would be a record of something that never happened.
  logAuditEvent({
    actionType: AUDIT_ACTION_TYPES.OPENPLAY_SESSION_CANCELLED,
    entityType: ENTITY_TYPES.OPENPLAY_SESSION,
    entityId: sessionId,
    statusOld: session.status || 'active',
    statusNew: OPEN_PLAY_SESSION_STATUS_CANCELLED,
    summary: {
      sportName: session.sportName || '',
      date: session.date,
      endDate: session.endDate || null,
      // Same { [court]: [slot, ...] } shape saveOpenPlaySession writes, so the
      // Audit Trail renders this session's courts and hours with the existing
      // "Court N: <hours>" lines rather than a second special case.
      slotsByCourt: Object.entries(getSessionSlotsWithDates(session)).reduce((acc, [court, slots]) => {
        acc[court] = slots.map(({ slot }) => slot);
        return acc;
      }, {}),
      maxPlayers: Number(session.maxPlayers) || 0,
      fee: Number(session.fee) || 0,
      cancelledJoins: affected.length,
      cancelledPlayers: players,
      type: 'openplay-session',
    },
  });

  // One entry per person as well as the session-level one: the trail is read
  // per guest as often as per session, and "why was I taken off?" has to be
  // answerable from the join's own history.
  affected.forEach((join) => {
    logAuditEvent({
      actionType: AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_REMOVED,
      entityType: ENTITY_TYPES.OPENPLAY_JOIN,
      entityId: join.id,
      statusOld: join.status || null,
      statusNew: OPEN_PLAY_STATUS_REMOVED,
      summary: buildOpenPlayJoinSummary(join),
    });
  });

  return { joins: affected.length, players };
};

// ---- Session display helpers (OpenPlaySessionCard.jsx, App.jsx's card-click handler) ----

/**
 * Sorted court numbers a session covers, e.g. [1, 2, 5]. Both sides of
 * midnight — a court used only after 12AM is still one of the session's
 * courts.
 */
export const getSessionCourtNumbers = (session) => [...new Set([
  ...Object.keys(session?.courts || {}),
  ...Object.keys(session?.courtsNextDay || {}),
])].map(Number).filter(Number.isFinite).sort((a, b) => a - b);

/**
 * Overall start/end hour across every court in a session, plus whether
 * every court actually shares that same range (courts can each have their
 * own start/end — see AdminHostOpenPlayModal's per-court time picker) —
 * used for the card's single-line time range vs. "· varies by court" flag.
 * @returns {{startHour:number|null, endHour:number|null, variesByCourt:boolean}}
 */
/**
 * { [court]: absoluteHour[] } for a session, where an hour after midnight
 * counts as 24+ rather than wrapping back round to 0.
 *
 * Everything that reasons about a session's SHAPE goes through this. Without
 * the anchoring, a 10PM->6AM session reads as starting at midnight
 * (min(22, 0) = 0) and every range built from it comes out backwards.
 */
const sessionCourtHourMap = (session) => {
  const courtHours = {};
  const collect = (map, dayOffset) => {
    Object.entries(map || {}).forEach(([court, slots]) => {
      (slots || []).forEach((slot) => {
        const key = String(court);
        if (!courtHours[key]) courtHours[key] = [];
        courtHours[key].push(parseTimeSlotStartHour(slot) + dayOffset * 24);
      });
    });
  };
  collect(session?.courts, 0);
  collect(session?.courtsNextDay, 1);
  return courtHours;
};

/**
 * One court's own start/end within a session — for the per-court lines shown
 * when the courts don't all share a range.
 * @returns {{startHour:number, endHour:number, endsNextDay:boolean}|null}
 */
export const getSessionCourtRange = (session, court) => {
  const hours = sessionCourtHourMap(session)[String(court)];
  if (!hours || hours.length === 0) return null;
  const sorted = [...hours].sort((a, b) => a - b);
  const endAbs = sorted[sorted.length - 1] + 1;
  return { startHour: sorted[0] % 24, endHour: endAbs % 24, endsNextDay: endAbs > 24 };
};

/**
 * "10:00 PM – 6:00 AM (next day)" — the one place the app words a session's
 * time range, so the card, the admin grid, the join modal and the confirm
 * dialog can't drift apart on whether they mention the next day at all.
 */
export const formatSessionRangeText = (startHour, endHour, endsNextDay = false) =>
  `${formatHourLabel(startHour)} – ${formatHourLabel(endHour)}${endsNextDay ? ' (next day)' : ''}`;

export const getSessionTimeRangeInfo = (session) => {
  const courtHours = sessionCourtHourMap(session);
  const courtEntries = Object.entries(courtHours);
  if (courtEntries.length === 0) return { startHour: null, endHour: null, variesByCourt: false };

  const ranges = courtEntries.map(([, hours]) => {
    const sorted = [...hours].sort((a, b) => a - b);
    return { startAbs: sorted[0], endAbs: sorted[sorted.length - 1] + 1 };
  });
  const startAbs = Math.min(...ranges.map(r => r.startAbs));
  const endAbs = Math.max(...ranges.map(r => r.endAbs));
  const variesByCourt = !ranges.every(r => r.startAbs === ranges[0].startAbs && r.endAbs === ranges[0].endAbs);
  // Handed back as clock hours, which is all the card renders. `endsNextDay`
  // is what lets it add the "(next day)" the bare number can't carry.
  return {
    startHour: startAbs % 24,
    endHour: endAbs % 24,
    variesByCourt,
    endsNextDay: endAbs > 24,
  };
};

/** "Wed, Aug 12, 2026" — the card's date heading, parsed from "yyyy-MM-dd" as a local date (not UTC) to avoid an off-by-one-day shift. */
export const formatSessionDateLabel = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * Multi-line plain-text summary of an entire hosted session (every court's
 * time range, fee, cap, notes) — shown in the guest "coming soon" info
 * modal when a session CARD is clicked (as opposed to a single grid cell,
 * which only needs that one cell's own date/time/court — see App.jsx's
 * handleJoinOpenPlaySlot).
 * @param {object} session
 * @returns {string}
 */
export const formatOpenPlaySessionDetails = (session, config = null) => {
  if (!session) return '';
  const courts = getSessionCourtNumbers(session);
  const { startHour, endHour, variesByCourt, endsNextDay } = getSessionTimeRangeInfo(session);
  const lines = [
    `${session.sportName || 'Court'} Open Play`,
    formatSessionDateLabel(session.date),
  ];
  if (variesByCourt) {
    courts.forEach((court) => {
      const range = getSessionCourtRange(session, court);
      if (!range) return;
      lines.push(`${getCourtLabel(config, court)}: ${formatSessionRangeText(range.startHour, range.endHour, range.endsNextDay)}`);
    });
  } else if (startHour !== null) {
    // Named courts are listed in full ("Center Court, Court 2") since a
    // bare "Courts A, B" reads oddly; unnamed ones keep the compact
    // "Courts 1, 2" form.
    const courtLabels = courts.map(c => getCourtLabel(config, c));
    const allDefault = courtLabels.every((label, i) => label === `Court ${courts[i]}`);
    const courtsText = allDefault
      ? `Court${courts.length > 1 ? 's' : ''} ${courts.join(', ')}`
      : courtLabels.join(', ');
    const timeText = formatSessionRangeText(startHour, endHour, endsNextDay);
    // Named courts ("Court, Training Balls (Set)") overrun the time line in
    // the join dialog and wrap mid-name, which reads as two unrelated
    // fragments. Past a threshold they get their own line instead. Same
    // rule and threshold as SessionDetails in OpenPlayModal.jsx, so the
    // confirm dialog and the modal it opens lay the line out identically.
    if (courtsText.length > OPEN_PLAY_COURTS_INLINE_MAX_CHARS) {
      lines.push(timeText);
      lines.push(courtsText);
    } else {
      lines.push(`${timeText} · ${courtsText}`);
    }
  }
  lines.push(`Fee: ${formatMoney(session.fee, config)} / player`);
  if (session.maxPlayers) lines.push(`Max players: ${session.maxPlayers}`);
  if (session.notes) lines.push(session.notes);
  return lines.join('\n');
};
